import { StrictMode } from "react";
import { hydrateRoot } from "react-dom/client";
import { createAuthenticationContext } from "@aviv-unified-frontend/runtime/authentication";
import { createErrorMessage } from "@aviv-unified-frontend/internal";
import { createEnvironmentContext } from "@aviv-unified-frontend/runtime/environment";

import { BRAND_CONFIGURATION } from "../configs/brands";
import { Shell } from "../src/Shell";
import type { ShellProps } from "../src/Shell";
import { getGlobalStore } from "../src/utils/useGlobalStore";
import { useGlobalStore } from "../src/utils/useGlobalStore/useGlobalStore";

import { initialApplication } from "./initialApplication";

const authenticationConfig = BRAND_CONFIGURATION.authentication ?? null;

const useEnvironmentContext: ShellProps["useEnvironmentContext"] = () => {
  const { applicationId } = useGlobalStore();

  return createEnvironmentContext({ application: { id: applicationId } });
};

export const mount = () => {
  const rootElement = window.document.getElementById("root");
  const initialDataScript = document.getElementById("__INITIAL_DATA__");

  const initialData: Pick<ShellProps, "headerFooterConfig"> = JSON.parse(
    initialDataScript?.textContent ?? "{}",
  );

  if (!rootElement) {
    throw new Error(
      createErrorMessage({
        cause: "Unable to mount the Shell container",
        solution:
          "The Shell container element is not found. Make sure to have a root element defined in the HTML",
      }),
    );
  }

  const { applicationId, errors } = getGlobalStore();

  hydrateRoot(
    rootElement,
    <StrictMode>
      <Shell
        {...initialData}
        authenticationContext={createAuthenticationContext({
          config: authenticationConfig,
          identityProvider: BRAND_CONFIGURATION.identityProvider,
        })}
        initialApplication={initialApplication}
        applicationId={applicationId}
        errors={errors}
        useEnvironmentContext={useEnvironmentContext}
      />
    </StrictMode>,
  );

  initialDataScript?.remove();
};
